import React from 'react';
import { NavLink } from 'react-router-dom';
import { useLocalStorage } from '../hooks/useLocalStorage';

const SynapseLogo = () => (
  <svg viewBox="0 0 100 100" className="h-8">
    <circle cx="50" cy="50" r="10" fill="#4F46E5"/>
    <circle cx="20" cy="50" r="8" fill="#6366F1"/>
    <circle cx="80" cy="50" r="8" fill="#818CF8"/>
    <circle cx="50" cy="20" r="8" fill="#6366F1"/>
    <circle cx="50" cy="80" r="8" fill="#818CF8"/>
    <line x1="50" y1="20" x2="50" y2="80" stroke="#4F46E5" strokeWidth="2"/>
    <line x1="20" y1="50" x2="80" y2="50" stroke="#4F46E5" strokeWidth="2"/>
  </svg>
);

const SynapseIcon = () => (
  <svg viewBox="0 0 100 100" className="h-8 w-8">
    <circle cx="50" cy="50" r="10" fill="#4F46E5"/>
    <circle cx="20" cy="50" r="8" fill="#6366F1"/>
    <circle cx="80" cy="50" r="8" fill="#818CF8"/>
    <circle cx="50" cy="20" r="8" fill="#6366F1"/>
    <circle cx="50" cy="80" r="8" fill="#818CF8"/>
    <line x1="50" y1="20" x2="50" y2="80" stroke="#4F46E5" strokeWidth="2"/>
    <line x1="20" y1="50" x2="80" y2="50" stroke="#4F46E5" strokeWidth="2"/>
  </svg>
);

const Sidebar: React.FC = () => {
  const [isExpanded, setIsExpanded] = useLocalStorage('sidebar-expanded', true);
  
  const menuItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: 'chart-line',
      description: 'Overview and analytics'
    },
    {
      name: 'Applications',
      path: '/applications',
      icon: 'cube',
      description: 'Manage your applications'
    },
    {
      name: 'Users',
      path: '/users',
      icon: 'users',
      description: 'User management'
    },
    {
      name: 'Health Programs',
      path: '/health/program-groups',
      icon: 'heartbeat',
      description: 'Manage health program groups'
    },
    {
      name: 'Chat Interface',
      path: '/chat',
      icon: 'comment-dots',
      description: 'Interactive chat system'
    },
  ];
  
  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div 
      className={`
        bg-[#0A0C1B] text-white
        ${isExpanded ? 'w-64' : 'w-20'} 
        min-h-screen flex flex-col 
        transition-all duration-300 ease-in-out
        border-r border-gray-800
      `}
    >
      <div className={`
        p-4 border-b border-gray-800 
        flex ${isExpanded ? 'justify-between' : 'justify-center'} 
        items-center
      `}>
        {isExpanded ? (
          <div className="flex items-center">
            <SynapseLogo />
            <span className="ml-2 font-semibold text-lg">Synapse</span>
          </div>
        ) : (
          <SynapseIcon />
        )}
        <button 
          onClick={toggleSidebar} 
          className="text-gray-400 hover:text-white transition-colors rounded-full p-2 hover:bg-gray-800"
          title={isExpanded ? "Collapse menu" : "Expand menu"}
        >
          <i className={`fas fa-chevron-${isExpanded ? 'left' : 'right'} text-sm`}></i>
        </button>
      </div>
      
      <nav className="flex-grow py-6">
        <ul className="space-y-1 px-3">
          {menuItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) => `
                  flex items-center 
                  ${isExpanded ? 'px-4 py-3' : 'p-3 justify-center'} 
                  rounded-lg transition-all duration-200
                  group relative
                  ${isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }
                `}
                title={!isExpanded ? item.name : undefined}
              >
                <span className={`${isExpanded ? 'mr-3' : ''} text-lg`}>
                  <i className={`fas fa-${item.icon}`}></i>
                </span>
                {isExpanded && (
                  <div>
                    <span className="font-medium">{item.name}</span>
                    <p className="text-xs text-gray-400 mt-0.5">{item.description}</p>
                  </div>
                )}
                
                {!isExpanded && (
                  <div className="
                    absolute left-full ml-3 px-2 py-1
                    bg-gray-900 text-white text-sm
                    rounded-md whitespace-nowrap
                    opacity-0 group-hover:opacity-100
                    pointer-events-none
                    transition-opacity duration-200
                    z-50
                  ">
                    <div className="font-medium">{item.name}</div>
                    <div className="text-xs text-gray-400">{item.description}</div>
                  </div>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      
      <div className={`
        p-4 border-t border-gray-800 
        ${isExpanded ? '' : 'text-center'}
        text-xs text-gray-500
      `}>
        {isExpanded ? (
          <div>
            <p className="font-medium">Synapse Admin</p>
            <p className="mt-1">Version 1.0.0</p>
          </div>
        ) : (
          <p>v1.0</p>
        )}
      </div>
    </div>
  );
};

export default Sidebar;